/**
 * Nord Labs Gaming - Main JavaScript
 * Minimal, performant interactions
 * Ported from Hydrogen storefront
 */

(function() {
  'use strict';

  /* ==========================================================================
     Utility Functions
     ========================================================================== */

  /**
   * Debounce function to limit how often a function can be called
   * @param {Function} func - Function to debounce
   * @param {number} wait - Milliseconds to wait
   * @returns {Function} - Debounced function
   */
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func.apply(this, args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  function shopifyRoute(path) {
    const root = window.Shopify?.routes?.root || '/';
    return root + path.replace(/^\/+/, '');
  }

  /* ==========================================================================
     OpenAI Ads Measurement
     ========================================================================== */

  const OpenAIAds = {
    checkoutStartedSent: false,

    init() {
      this.trackProductView();

      document.addEventListener('click', (e) => {
        const checkoutControl = e.target.closest('button[name="checkout"], input[name="checkout"], a[href*="/checkout"]');
        if (checkoutControl) {
          this.trackCheckoutStarted();
        }
      }, { capture: true });

      document.addEventListener('submit', (e) => {
        const submitter = e.submitter || document.activeElement;
        const form = e.target.closest('form');
        if (
          submitter?.getAttribute?.('name') === 'checkout' ||
          (form && form.querySelector('button[name="checkout"], input[name="checkout"]') && !form.querySelector('[name="update"]'))
        ) {
          this.trackCheckoutStarted();
        }
      }, { capture: true });
    },

    measure(eventName, data, options) {
      if (typeof window.oaiq !== 'function') return;

      try {
        if (options) {
          window.oaiq('measure', eventName, data, options);
        } else {
          window.oaiq('measure', eventName, data);
        }
      } catch (error) {
        console.warn('OpenAI Ads measurement failed:', error);
      }
    },

    getCurrency() {
      return window.NordLabsOpenAIAds?.currency || 'USD';
    },

    parseProductJson() {
      const productJson = document.querySelector('[data-product-page] [data-product-json], [data-product-json]');
      if (!productJson) return null;

      try {
        return JSON.parse(productJson.textContent);
      } catch (error) {
        return null;
      }
    },

    getSelectedVariant(product, form) {
      if (!product?.variants?.length) return null;

      const variantInput = form?.querySelector('[name="id"]') ||
        document.querySelector('[data-product-page] [data-variant-id], form[action*="/cart/add"] [name="id"]');
      const variantId = variantInput?.value;

      if (variantId) {
        const selectedVariant = product.variants.find((variant) => String(variant.id) === String(variantId));
        if (selectedVariant) return selectedVariant;
      }

      return product.variants.find((variant) => variant.available) || product.variants[0];
    },

    buildContentsItem(product, variant, quantity) {
      const amount = Number(variant?.price || product?.price || 0) || undefined;
      const id = variant?.sku || variant?.id || product?.id;
      const name = product?.title || variant?.name || variant?.title;

      return {
        id: String(id),
        name: name || 'NordLabs product',
        content_type: 'product',
        quantity: Math.max(parseInt(quantity, 10) || 1, 1),
        ...(amount ? { amount } : {}),
        currency: this.getCurrency()
      };
    },

    buildProductEventData(product, variant, quantity) {
      const item = this.buildContentsItem(product, variant, quantity);
      const amount = item.amount ? item.amount * item.quantity : undefined;

      return {
        type: 'contents',
        ...(amount ? { amount } : {}),
        currency: this.getCurrency(),
        contents: [item]
      };
    },

    buildAddedItemEventData(item, fallback) {
      const quantity = Math.max(parseInt(item?.quantity || fallback?.quantity, 10) || 1, 1);
      const amount = Number(item?.price || fallback?.price || 0) || undefined;
      const id = item?.sku || item?.variant_id || item?.id || fallback?.id;
      const name = item?.product_title || item?.title || fallback?.title || 'NordLabs product';

      return {
        type: 'contents',
        ...(amount ? { amount: amount * quantity } : {}),
        currency: this.getCurrency(),
        contents: [{
          id: String(id),
          name,
          content_type: 'product',
          quantity,
          ...(amount ? { amount } : {}),
          currency: this.getCurrency()
        }]
      };
    },

    trackProductView() {
      const product = this.parseProductJson();
      if (!product) return;

      const variant = this.getSelectedVariant(product);
      this.measure('contents_viewed', this.buildProductEventData(product, variant, 1));
    },

    trackItemsAdded(item, fallback) {
      this.measure('items_added', this.buildAddedItemEventData(item, fallback));
    },

    trackItemsAddedFromForm(form, item) {
      const formData = new FormData(form);
      const product = this.parseProductJson();
      const variant = this.getSelectedVariant(product, form);
      const quantity = formData.get('quantity') || item?.quantity || 1;

      if (product && variant) {
        this.measure('items_added', this.buildProductEventData(product, variant, quantity));
        return;
      }

      this.trackItemsAdded(item, {
        id: formData.get('id'),
        quantity
      });
    },

    trackCheckoutStarted() {
      if (this.checkoutStartedSent) return;

      this.checkoutStartedSent = true;
      this.measure('checkout_started', { type: 'contents' });
    }
  };

  /* ==========================================================================
     Header Hide on Scroll (Mobile Only)
     Matches Hydrogen useScrollDirection hook
     ========================================================================== */

  const HeaderScroll = {
    wrapper: null,
    lastScrollY: 0,
    ticking: false,
    threshold: 10,

    init() {
      this.wrapper = document.querySelector('[data-header-wrapper]');
      if (!this.wrapper) return;

      // Only attach scroll listener
      window.addEventListener('scroll', () => this.onScroll(), { passive: true });
      // Debounced resize handler for better performance
      window.addEventListener('resize', debounce(() => this.checkMobile(), 150), { passive: true });
    },

    checkMobile() {
      const isMobile = window.matchMedia('(max-width: 768px)').matches;
      if (!isMobile) {
        this.wrapper.classList.remove('header-hidden');
      }
      return isMobile;
    },

    onScroll() {
      if (!this.checkMobile()) return;
      
      if (!this.ticking) {
        window.requestAnimationFrame(() => this.updateScrollDirection());
        this.ticking = true;
      }
    },

    updateScrollDirection() {
      const scrollY = window.scrollY;
      const difference = scrollY - this.lastScrollY;

      // Only trigger if scrolled more than threshold
      if (Math.abs(difference) < this.threshold) {
        this.ticking = false;
        return;
      }

      // Always show when near top of page
      if (scrollY < 50) {
        this.wrapper.classList.remove('header-hidden');
      } else if (difference > 0) {
        // Scrolling down - hide header
        this.wrapper.classList.add('header-hidden');
      } else {
        // Scrolling up - show header
        this.wrapper.classList.remove('header-hidden');
      }

      this.lastScrollY = scrollY;
      this.ticking = false;
    }
  };

  /* ==========================================================================
     Cart Drawer
     ========================================================================== */

  const CartDrawer = {
    overlay: null,
    cartBoosterAssignment: {
      variant: 'B',
      testId: 'warranty-default-on-live'
    },
    warrantyAutoAddInFlight: false,
    
    init() {
      this.overlay = document.getElementById('cart-drawer');
      if (!this.overlay) return;

      // Use event delegation for better performance
      // Single document-level listener for cart toggle/close and other controls
      document.addEventListener('click', (e) => {
        // Cart drawer toggle
        const toggle = e.target.closest('[data-cart-drawer-toggle]');
        if (toggle) {
          e.preventDefault();
          this.open();
          return;
        }
        
        // Cart drawer close
        const close = e.target.closest('[data-cart-drawer-close]');
        if (close) {
          this.close();
          return;
        }
      });

      // Close on escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      });

      // Quantity controls (delegated on overlay)
      this.overlay.addEventListener('click', (e) => {
        const minus = e.target.closest('[data-quantity-minus]');
        const plus = e.target.closest('[data-quantity-plus]');
        const remove = e.target.closest('[data-remove-item]');
        const warrantyToggle = e.target.closest('[data-warranty-toggle]');
        const upsellAdd = e.target.closest('[data-upsell-add]');

        if (minus) this.updateQuantity(minus, -1);
        if (plus) this.updateQuantity(plus, 1);
        if (remove) this.removeItem(remove);
        if (warrantyToggle) this.toggleWarranty(warrantyToggle);
        if (upsellAdd) this.addUpsellItem(upsellAdd);
      });

      document.addEventListener('cartbooster:assignment', (event) => {
        const detail = event.detail || {};
        if (detail.surface !== 'cart_drawer') return;

        this.cartBoosterAssignment = detail.assignment || null;
        this.maybeAutoAddWarranty();
      });

      document.addEventListener('cart:refreshed', () => {
        this.maybeAutoAddWarranty();
      });
    },

    open() {
      if (!this.overlay) return;
      this.overlay.classList.add('open');
      this.overlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      
      // Hide header on mobile when cart is open
      const headerWrapper = document.querySelector('[data-header-wrapper]');
      if (headerWrapper) {
        headerWrapper.classList.add('header-cart-open');
      }

      // Focus trap - focus first focusable element
      const closeBtn = this.overlay.querySelector('[data-cart-drawer-close]');
      if (closeBtn) {
        setTimeout(() => closeBtn.focus(), 100);
      }

      this.maybeAutoAddWarranty();
    },

    close() {
      if (!this.overlay) return;
      this.overlay.classList.remove('open');
      this.overlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      
      // Show header again
      const headerWrapper = document.querySelector('[data-header-wrapper]');
      if (headerWrapper) {
        headerWrapper.classList.remove('header-cart-open');
      }
    },

    isOpen() {
      return this.overlay?.classList.contains('open');
    },

    async updateQuantity(btn, delta) {
      const item = btn.closest('[data-cart-item]');
      const key = item?.dataset.key;
      const input = item?.querySelector('[data-quantity-input]');
      
      if (!key || !input) return;

      // Show loading state
      item.classList.add('loading');

      // Get current quantity - handle both input and span elements
      const currentQty = parseInt(input.value || input.textContent) || 1;
      const newQty = Math.max(0, currentQty + delta);
      
      // Update display
      if (input.tagName === 'INPUT') {
        input.value = newQty;
      } else {
        input.textContent = newQty;
      }

      try {
        const response = await fetch(shopifyRoute('cart/change.js'), {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          credentials: 'same-origin',
          body: JSON.stringify({ id: key, quantity: newQty })
        });

        if (response.ok) {
          this.dispatchCartCommerce(newQty === 0 ? 'cart_item_remove' : 'cart_quantity_change', { actionId: btn.dataset.cartAnalyticsAction, fromQuantity: currentQty, quantity: newQty });
          this.refreshCart();
        } else {
          this.dispatchCartCommerce('cart_update_failure', { actionId: btn.dataset.cartAnalyticsAction, errorCode: 'http_error' });
        }
      } catch (error) {
        this.dispatchCartCommerce('cart_update_failure', { actionId: btn.dataset.cartAnalyticsAction, errorCode: 'network_error' });
        console.error('Failed to update cart:', error);
        item.classList.remove('loading');
      }
    },

    async removeItem(btn) {
      const item = btn.closest('[data-cart-item]');
      const key = item?.dataset.key;
      
      if (!key) return;

      // Show loading state
      item.classList.add('loading');

      try {
        const response = await fetch(shopifyRoute('cart/change.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 })
        });

        if (response.ok) {
          this.dispatchCartCommerce('cart_item_remove', { actionId: btn.dataset.cartAnalyticsAction, quantity: 0 });
          this.refreshCart();
        } else {
          this.dispatchCartCommerce('cart_update_failure', { actionId: btn.dataset.cartAnalyticsAction, errorCode: 'http_error' });
        }
      } catch (error) {
        this.dispatchCartCommerce('cart_update_failure', { actionId: btn.dataset.cartAnalyticsAction, errorCode: 'network_error' });
        console.error('Failed to remove item:', error);
        item.classList.remove('loading');
      }
    },

    async refreshCart() {
      try {
        // Fetch updated cart drawer section
        const response = await fetch(shopifyRoute('?sections=cart-drawer'));
        const data = await response.json();
        
        if (data['cart-drawer']) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(data['cart-drawer'], 'text/html');
          
          // Update cart drawer content
          const newContent = doc.getElementById('cart-drawer-content');
          const currentContent = document.getElementById('cart-drawer-content');
          if (newContent && currentContent) {
            currentContent.innerHTML = newContent.innerHTML;
          }

          // Update footer/summary - check if it exists in new content
          const newFooter = doc.getElementById('cart-drawer-footer');
          const currentFooter = document.getElementById('cart-drawer-footer');
          const cartDrawer = document.querySelector('.cart-drawer');
          
          if (newFooter) {
            if (currentFooter) {
              currentFooter.innerHTML = newFooter.innerHTML;
            } else if (cartDrawer) {
              // Footer doesn't exist yet, cart was empty before - append it
              const footerDiv = document.createElement('div');
              footerDiv.className = 'cart-drawer-footer';
              footerDiv.id = 'cart-drawer-footer';
              footerDiv.innerHTML = newFooter.innerHTML;
              cartDrawer.appendChild(footerDiv);
            }
          } else if (currentFooter) {
            // Cart is now empty, remove footer
            currentFooter.remove();
          }
        }

        // Update cart count in header
        const cartResponse = await fetch(shopifyRoute('cart.js'));
        const cart = await cartResponse.json();
        this.updateCartCount(cart.item_count);
        document.dispatchEvent(new CustomEvent('cart:refreshed', {
          detail: { cart }
        }));
      } catch (error) {
        console.error('Failed to refresh cart:', error);
      }
    },

    updateCartCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = count;
        el.style.display = count > 0 ? 'flex' : 'none';
      });
    },

    getWarrantyAutoOptOutKey() {
      const testId = this.cartBoosterAssignment?.testId;
      return testId ? `cartbooster_warranty_auto_off:${testId}` : null;
    },

    hasWarrantyAutoOptedOut() {
      const key = this.getWarrantyAutoOptOutKey();
      if (!key) return false;
      if (localStorage.getItem(key) === 'true') return true;

      // Preserve opt-outs made during the experiment when B becomes permanent.
      for (let index = 0; index < localStorage.length; index += 1) {
        const existingKey = localStorage.key(index);
        if (
          existingKey?.startsWith('cartbooster_warranty_auto_off:') &&
          localStorage.getItem(existingKey) === 'true'
        ) {
          localStorage.setItem(key, 'true');
          return true;
        }
      }

      return false;
    },

    setWarrantyAutoOptOut(value) {
      const key = this.getWarrantyAutoOptOutKey();
      if (!key) return;

      if (value) {
        localStorage.setItem(key, 'true');
      } else {
        const keysToRemove = [];
        for (let index = 0; index < localStorage.length; index += 1) {
          const existingKey = localStorage.key(index);
          if (existingKey?.startsWith('cartbooster_warranty_auto_off:')) {
            keysToRemove.push(existingKey);
          }
        }
        keysToRemove.forEach((existingKey) => localStorage.removeItem(existingKey));
      }
    },

    dispatchCartBoosterTrack(eventType, metadata = {}) {
      document.dispatchEvent(new CustomEvent('cartbooster:track', {
        detail: {
          surface: 'cart_drawer',
          eventType,
          metadata
        }
      }));
    },

    dispatchCartCommerce(eventType, metadata = {}) {
      document.dispatchEvent(new CustomEvent('cartbooster:commerce', { detail: { eventType, ...metadata } }));
    },

    async maybeAutoAddWarranty() {
      const assignment = this.cartBoosterAssignment;
      if (!this.overlay || !this.isOpen() || this.warrantyAutoAddInFlight) return;
      if (!assignment || assignment.variant !== 'B' || this.hasWarrantyAutoOptedOut()) return;

      const warrantyToggle = this.overlay.querySelector('[data-warranty-toggle]');
      const variantId = warrantyToggle?.dataset.warrantyVariant;
      if (!warrantyToggle || !variantId || warrantyToggle.dataset.warrantyAdded === 'true') return;

      this.warrantyAutoAddInFlight = true;
      warrantyToggle.classList.add('cart-protection--loading');

      try {
        const cartResponse = await fetch(shopifyRoute('cart.js'));
        const cart = await cartResponse.json();
        const warrantyVariantId = parseInt(variantId, 10);
        const hasWarranty = cart.items?.some(item => item.variant_id === warrantyVariantId);
        const hasNonWarrantyItem = cart.items?.some(item => item.variant_id !== warrantyVariantId);

        if (hasWarranty || !hasNonWarrantyItem) return;

        const response = await fetch(shopifyRoute('cart/add.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: warrantyVariantId,
              quantity: 1,
              properties: {
                _cartbooster_default_on: assignment.testId
              }
            }]
          })
        });

        if (!response.ok) throw new Error('Failed to auto-add warranty');

        let addedItem = null;
        try {
          addedItem = await response.clone().json();
        } catch (error) {
          addedItem = null;
        }

        OpenAIAds.trackItemsAdded(addedItem?.items?.[0] || addedItem, { id: variantId, quantity: 1 });
        this.dispatchCartBoosterTrack('warranty_auto_add', {
          variantId,
          source: 'default_on'
        });
        this.dispatchCartCommerce('warranty_auto_add', { variantId: String(variantId), quantity: 1 });
        this.refreshCart();
      } catch (error) {
        this.dispatchCartCommerce('cart_update_failure', { variantId: String(variantId), offerId: 'warranty:' + variantId, errorCode: 'unknown' });
        console.error('Failed to auto-add warranty:', error);
        warrantyToggle.classList.remove('cart-protection--loading');
      } finally {
        this.warrantyAutoAddInFlight = false;
      }
    },

    async toggleWarranty(el) {
      const variantId = el.dataset.warrantyVariant;
      const isAdded = el.dataset.warrantyAdded === 'true';
      const warrantyKey = el.dataset.warrantyKey;
      
      if (!variantId) return;

      // Show loading state
      el.classList.add('cart-protection--loading');

      try {
        if (isAdded && warrantyKey) {
          // Remove warranty using the stored key
          const response = await fetch(shopifyRoute('cart/change.js'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: warrantyKey, quantity: 0 })
          });
          if (!response.ok) throw new Error('Failed to remove warranty');
          this.setWarrantyAutoOptOut(true);
          this.dispatchCartBoosterTrack('warranty_remove', {
            variantId,
            source: 'manual_toggle'
          });
          this.dispatchCartCommerce('warranty_remove', { actionId: el.dataset.cartAnalyticsAction, variantId: String(variantId), quantity: 0 });
        } else if (isAdded) {
          // Remove warranty - find the key first
          const cartResponse = await fetch(shopifyRoute('cart.js'));
          const cart = await cartResponse.json();
          
          const warrantyItem = cart.items.find(item => 
            item.variant_id === parseInt(variantId)
          );
          
          if (warrantyItem) {
            const response = await fetch(shopifyRoute('cart/change.js'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: warrantyItem.key, quantity: 0 })
            });
            if (!response.ok) throw new Error('Failed to remove warranty');
            this.setWarrantyAutoOptOut(true);
            this.dispatchCartBoosterTrack('warranty_remove', {
              variantId,
              source: 'manual_toggle'
            });
          this.dispatchCartCommerce('warranty_remove', { actionId: el.dataset.cartAnalyticsAction, variantId: String(variantId), quantity: 0 });
          }
        } else {
          // Add warranty
          const response = await fetch(shopifyRoute('cart/add.js'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [{ id: parseInt(variantId), quantity: 1 }]
            })
          });

          if (response.ok) {
            let addedItem = null;
            try {
              addedItem = await response.clone().json();
            } catch (error) {
              addedItem = null;
            }
            OpenAIAds.trackItemsAdded(addedItem?.items?.[0] || addedItem, { id: variantId, quantity: 1 });
            this.setWarrantyAutoOptOut(false);
            this.dispatchCartBoosterTrack('warranty_add', {
              variantId,
              source: 'manual_toggle'
            });
          this.dispatchCartCommerce('warranty_add', { actionId: el.dataset.cartAnalyticsAction, variantId: String(variantId), quantity: 1 });
          } else {
            this.dispatchCartCommerce('cart_update_failure', { actionId: el.dataset.cartAnalyticsAction, variantId: String(variantId), offerId: 'warranty:' + variantId, errorCode: 'http_error' });
          }
        }

        this.refreshCart();
      } catch (error) {
        this.dispatchCartCommerce('cart_update_failure', { actionId: el.dataset.cartAnalyticsAction, variantId: String(variantId), offerId: 'warranty:' + variantId, errorCode: 'unknown' });
        console.error('Failed to toggle warranty:', error);
        el.classList.remove('cart-protection--loading');
      }
    },

    async addUpsellItem(btn) {
      const variantId = btn.dataset.variantId;
      const upsellItem = btn.closest('[data-upsell-item]');
      
      if (!variantId || !upsellItem) return;

      // Show loading state
      upsellItem.classList.add('cart-upsell-adding');
      btn.disabled = true;

      try {
        const response = await fetch(shopifyRoute('cart/add.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: parseInt(variantId), quantity: 1 }]
          })
        });

        if (response.ok) {
          let addedItem = null;
          try {
            addedItem = await response.clone().json();
          } catch (error) {
            addedItem = null;
          }
          OpenAIAds.trackItemsAdded(addedItem?.items?.[0] || addedItem, { id: variantId, quantity: 1 });

          this.dispatchCartCommerce('upsell_add_success', { actionId: btn.dataset.cartAnalyticsAction, variantId: String(variantId), offerId: 'addon:' + variantId, quantity: 1 });

          // Animate out the upsell item
          upsellItem.classList.remove('cart-upsell-adding');
          upsellItem.classList.add('cart-upsell-added');
          
          // Refresh cart after animation
          setTimeout(() => {
            this.refreshCart();
          }, 300);
        } else {
          throw new Error('Failed to add upsell item');
        }
      } catch (error) {
        this.dispatchCartCommerce('upsell_add_failure', { actionId: btn.dataset.cartAnalyticsAction, variantId: String(variantId), offerId: 'addon:' + variantId, errorCode: 'unknown' });
        console.error('Failed to add upsell item:', error);
        upsellItem.classList.remove('cart-upsell-adding');
        btn.disabled = false;
      }
    }
  };

  /* ==========================================================================
     Mobile Menu
     ========================================================================== */

  const MobileMenu = {
    overlay: null,
    toggleBtn: null,

    init() {
      this.overlay = document.querySelector('[data-mobile-menu-overlay]');
      this.toggleBtn = document.querySelector('[data-mobile-menu-toggle]');
      
      if (!this.overlay) return;

      // Use event delegation for all mobile menu interactions
      document.addEventListener('click', (e) => {
        // Toggle button
        const toggle = e.target.closest('[data-mobile-menu-toggle]');
        if (toggle) {
          this.toggle();
          return;
        }
        
        // Close button
        const close = e.target.closest('[data-mobile-menu-close]');
        if (close) {
          this.close();
          return;
        }
        
        // Nav links - close menu after navigation
        const navLink = e.target.closest('.mobile-nav-link');
        if (navLink && this.isOpen()) {
          setTimeout(() => this.close(), 100);
        }
      });

      // Close on escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      });
    },

    toggle() {
      if (this.isOpen()) {
        this.close();
      } else {
        this.open();
      }
    },

    open() {
      if (!this.overlay) return;
      
      this.overlay.classList.add('open');
      this.overlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      
      // Update toggle button icon and aria
      if (this.toggleBtn) {
        this.toggleBtn.setAttribute('aria-expanded', 'true');
        this.toggleBtn.setAttribute('aria-label', 'Close menu');
        
        const menuIcon = this.toggleBtn.querySelector('.icon-menu');
        const closeIcon = this.toggleBtn.querySelector('.icon-close');
        if (menuIcon) menuIcon.style.display = 'none';
        if (closeIcon) closeIcon.style.display = 'block';
      }

      // Focus first link
      const firstLink = this.overlay.querySelector('.mobile-nav-link');
      if (firstLink) {
        setTimeout(() => firstLink.focus(), 100);
      }
    },

    close() {
      if (!this.overlay) return;
      
      this.overlay.classList.remove('open');
      this.overlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      
      // Update toggle button icon and aria
      if (this.toggleBtn) {
        this.toggleBtn.setAttribute('aria-expanded', 'false');
        this.toggleBtn.setAttribute('aria-label', 'Open menu');
        
        const menuIcon = this.toggleBtn.querySelector('.icon-menu');
        const closeIcon = this.toggleBtn.querySelector('.icon-close');
        if (menuIcon) menuIcon.style.display = 'block';
        if (closeIcon) closeIcon.style.display = 'none';
      }
    },

    isOpen() {
      return this.overlay?.classList.contains('open');
    }
  };

  /* ==========================================================================
     Product Gallery
     ========================================================================== */

  const ProductGallery = {
    init() {
      const galleries = document.querySelectorAll('[data-product-gallery]');
      
      galleries.forEach(gallery => {
        const thumbs = gallery.querySelectorAll('[data-gallery-thumb]');
        const mainImage = gallery.querySelector('[data-gallery-main-image]');
        
        if (!thumbs.length || !mainImage) return;

        // Use event delegation for thumbnail clicks
        gallery.addEventListener('click', (e) => {
          const thumb = e.target.closest('[data-gallery-thumb]');
          if (!thumb) return;
          
          // Update active state
          thumbs.forEach(t => t.classList.remove('active'));
          thumb.classList.add('active');
          
          // Update main image
          const newSrc = thumb.dataset.imageSrc;
          const newSrcset = thumb.dataset.imageSrcset;
          
          if (newSrc) {
            mainImage.src = newSrc;
            if (newSrcset) mainImage.srcset = newSrcset;
          }
        });

        // Touch/swipe support for mobile
        this.initSwipe(gallery, thumbs);
      });
    },

    initSwipe(gallery, thumbs) {
      const mainImageWrapper = gallery.querySelector('[data-gallery-main]');
      if (!mainImageWrapper || thumbs.length < 2) return;

      let touchStartX = 0;
      let touchEndX = 0;
      let currentIndex = 0;

      mainImageWrapper.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0].screenX;
      }, { passive: true });

      mainImageWrapper.addEventListener('touchend', (e) => {
        touchEndX = e.changedTouches[0].screenX;
        this.handleSwipe(thumbs, touchStartX, touchEndX, currentIndex, (newIndex) => {
          currentIndex = newIndex;
        });
      }, { passive: true });
    },

    handleSwipe(thumbs, startX, endX, currentIndex, setIndex) {
      const swipeThreshold = 50;
      const diff = startX - endX;
      
      if (Math.abs(diff) < swipeThreshold) return;

      let newIndex = currentIndex;
      
      if (diff > 0) {
        // Swipe left - next image
        newIndex = Math.min(currentIndex + 1, thumbs.length - 1);
      } else {
        // Swipe right - previous image
        newIndex = Math.max(currentIndex - 1, 0);
      }

      if (newIndex !== currentIndex) {
        thumbs[newIndex].click();
        setIndex(newIndex);
      }
    }
  };

  /* ==========================================================================
     Add to Cart
     ========================================================================== */

  const AddToCart = {
    init() {
      // Use event delegation for add-to-cart forms
      document.addEventListener('submit', async (e) => {
        const form = e.target.closest('[data-add-to-cart-form]');
        if (!form) return;
        
        e.preventDefault();
        
        const submitBtn = form.querySelector('[type="submit"]');
        const originalText = submitBtn?.textContent;
        
        // Show loading state
        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.textContent = 'Adding...';
        }

        const formData = new FormData(form);
        const data = {
          id: formData.get('id'),
          quantity: parseInt(formData.get('quantity')) || 1
        };

        try {
          const response = await fetch(shopifyRoute('cart/add.js'), {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            credentials: 'same-origin',
            body: JSON.stringify(data)
          });

          if (response.ok) {
            let addedItem = null;
            try {
              addedItem = await response.clone().json();
            } catch (error) {
              addedItem = null;
            }
            OpenAIAds.trackItemsAddedFromForm(form, addedItem);

            // Open cart drawer and refresh
            CartDrawer.open();
            CartDrawer.refreshCart();
            
            // Show success state briefly
            if (submitBtn) {
              submitBtn.textContent = 'Added!';
              setTimeout(() => {
                submitBtn.disabled = false;
                submitBtn.textContent = originalText;
              }, 1500);
            }
          } else {
            throw new Error('Failed to add to cart');
          }
        } catch (error) {
          console.error('Failed to add to cart:', error);
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = originalText;
          }
        }
      });
    }
  };

  /* ==========================================================================
     Newsletter Form Enhancement
     ========================================================================== */

  const NewsletterForm = {
    init() {
      // Use event delegation for newsletter forms
      document.addEventListener('submit', (e) => {
        const form = e.target.closest('.footer-newsletter-form');
        if (!form) return;
        
        const btn = form.querySelector('.footer-newsletter-btn');
        const arrowIcon = btn?.querySelector('.newsletter-icon-arrow');
        const loading = btn?.querySelector('.newsletter-loading');

        // Show loading state
        if (arrowIcon) arrowIcon.style.display = 'none';
        if (loading) loading.style.display = 'inline';
      });
    }
  };

  /* ==========================================================================
     Variant Selection
     ========================================================================== */

  const VariantSelection = {
    init() {
      // Use event delegation for variant selection
      document.addEventListener('change', (e) => {
        const select = e.target.closest('[data-variant-select]');
        if (select) {
          this.updateVariant(select);
        }
      });

      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-option-btn]');
        if (btn) {
          e.preventDefault();
          this.selectOption(btn);
        }
      });
    },

    updateVariant(select) {
      const form = select.closest('form');
      const variantId = select.value;
      
      // Update hidden variant input
      const variantInput = form?.querySelector('[name="id"]');
      if (variantInput) {
        variantInput.value = variantId;
      }

      // Update URL without reload
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url);

      // Trigger custom event for price updates etc.
      document.dispatchEvent(new CustomEvent('variant:changed', {
        detail: { variantId, select }
      }));
    },

    selectOption(btn) {
      const group = btn.closest('[data-option-group]');
      if (!group) return;

      // Update active state
      group.querySelectorAll('[data-option-btn]').forEach(b => {
        b.classList.remove('active');
      });
      btn.classList.add('active');

      // Find matching variant
      this.findVariant(btn.closest('form'));
    },

    findVariant(form) {
      if (!form) return;

      const options = [];
      form.querySelectorAll('[data-option-group]').forEach(group => {
        const activeBtn = group.querySelector('[data-option-btn].active');
        if (activeBtn) {
          options.push(activeBtn.dataset.value);
        }
      });

      // Find variant matching all selected options
      const variantSelect = form.querySelector('[data-variant-select]');
      if (variantSelect) {
        const matchingOption = Array.from(variantSelect.options).find(opt => {
          const variantOptions = opt.dataset.options?.split(',') || [];
          return options.every((o, i) => variantOptions[i] === o);
        });

        if (matchingOption) {
          variantSelect.value = matchingOption.value;
          variantSelect.dispatchEvent(new Event('change'));
        }
      }
    }
  };

  /* ==========================================================================
     Lazy Loading with Intersection Observer
     Performance optimization for images and animations
     ========================================================================== */

  const LazyLoader = {
    observer: null,

    init() {
      // Skip if IntersectionObserver not supported
      if (!('IntersectionObserver' in window)) {
        this.loadAllImages();
        return;
      }

      // Create observer for lazy loading
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.loadImage(entry.target);
            this.observer.unobserve(entry.target);
          }
        });
      }, {
        rootMargin: '50px 0px', // Load images 50px before they enter viewport
        threshold: 0.01
      });

      // Observe all lazy images
      this.observeImages();
    },

    observeImages() {
      // Target images with data-src (for deferred loading) or loading="lazy" that may need JS help
      document.querySelectorAll('img[data-lazy-src], [data-lazy-load]').forEach(img => {
        this.observer.observe(img);
      });
    },

    loadImage(img) {
      const src = img.dataset.lazySrc || img.dataset.src;
      const srcset = img.dataset.lazySrcset || img.dataset.srcset;
      
      if (src) {
        img.src = src;
        img.removeAttribute('data-lazy-src');
        img.removeAttribute('data-src');
      }
      
      if (srcset) {
        img.srcset = srcset;
        img.removeAttribute('data-lazy-srcset');
        img.removeAttribute('data-srcset');
      }
      
      img.classList.add('loaded');
      img.removeAttribute('data-lazy-load');
    },

    loadAllImages() {
      // Fallback for browsers without IntersectionObserver
      document.querySelectorAll('img[data-lazy-src], [data-lazy-load]').forEach(img => {
        this.loadImage(img);
      });
    },

    // Method to observe dynamically added content
    observeNewContent(container) {
      if (!this.observer || !container) return;
      
      container.querySelectorAll('img[data-lazy-src], [data-lazy-load]').forEach(img => {
        this.observer.observe(img);
      });
    }
  };

  /* ==========================================================================
     Scroll Animation Observer
     Trigger animations when elements enter viewport
     ========================================================================== */

  const ScrollAnimations = {
    observer: null,

    init() {
      if (!('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            // Optionally unobserve after animation (one-time)
            if (entry.target.dataset.animateOnce !== 'false') {
              this.observer.unobserve(entry.target);
            }
          }
        });
      }, {
        rootMargin: '-10% 0px', // Trigger slightly after entering viewport
        threshold: 0.1
      });

      // Observe elements with animation classes
      document.querySelectorAll('[data-animate-on-scroll]').forEach(el => {
        this.observer.observe(el);
      });
    }
  };

  /* ==========================================================================
     Thumbnail Bracket Exit Animation
     ==========================================================================
     Watches `.manual-thumb` elements globally. When `.manual-thumb-active` is
     removed (user selected a different thumb), tag the outgoing thumb with
     `.manual-thumb-leaving` for 520ms so CSS can play the reverse line-trace. */

  const ThumbBracketExit = {
    init() {
      const EXIT_DURATION = 520;
      const observer = new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (m.attributeName !== 'class') continue;
          const el = m.target;
          if (!el.classList || !el.classList.contains('manual-thumb')) continue;
          const oldClasses = (m.oldValue || '').split(/\s+/);
          const hadActive = oldClasses.indexOf('manual-thumb-active') !== -1;
          const hasActive = el.classList.contains('manual-thumb-active');
          if (hadActive && !hasActive) {
            el.classList.add('manual-thumb-leaving');
            setTimeout(() => el.classList.remove('manual-thumb-leaving'), EXIT_DURATION);
          }
        }
      });
      observer.observe(document.body, {
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
        attributeOldValue: true,
      });
    }
  };

  /* ==========================================================================
     Image SEO Attributes
     ========================================================================== */

  const ImageSeoAttributes = {
    init() {
      this.apply(document);

      if (!('MutationObserver' in window)) return;

      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType !== Node.ELEMENT_NODE) return;
            this.apply(node);
          });
        });
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    },

    apply(root) {
      const images = root.matches?.('img') ? [root] : root.querySelectorAll?.('img') || [];

      images.forEach((image) => {
        const alt = image.getAttribute('alt')?.trim();
        if (alt && !image.hasAttribute('title')) {
          image.setAttribute('title', alt);
        }

        if (image.hasAttribute('width') && image.hasAttribute('height')) return;

        const applyNaturalDimensions = () => {
          if (!image.naturalWidth || !image.naturalHeight) return;
          if (!image.hasAttribute('width')) image.setAttribute('width', String(image.naturalWidth));
          if (!image.hasAttribute('height')) image.setAttribute('height', String(image.naturalHeight));
        };

        if (image.complete) {
          applyNaturalDimensions();
        } else {
          image.addEventListener('load', applyNaturalDimensions, { once: true });
        }
      });
    }
  };

  /* ==========================================================================
     Initialize
     ========================================================================== */

  document.addEventListener('DOMContentLoaded', () => {
    OpenAIAds.init();
    HeaderScroll.init();
    CartDrawer.init();
    MobileMenu.init();
    ProductGallery.init();
    AddToCart.init();
    NewsletterForm.init();
    VariantSelection.init();
    LazyLoader.init();
    ScrollAnimations.init();
    ThumbBracketExit.init();
    ImageSeoAttributes.init();
  });

  // Make modules available globally for dynamic content
  window.NordLabs = {
    OpenAIAds,
    CartDrawer,
    MobileMenu,
    LazyLoader,
    ScrollAnimations,
    ImageSeoAttributes
  };

})();
